import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Link } from '@tanstack/react-router';
import { CheckCircle2, CircleAlert, CloudUpload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip } from '@/components/ui/tooltip';
import { useToast } from '@/components/ui/toast';
import { isActive, uploadManager, type UploadTask } from '@/lib/upload-manager';
import { errorMessage } from '@/lib/api';
import { formatBytes } from '@/lib/format';
import { cn } from '@/lib/cn';

/**
 * The header's view of every upload this browser has started: progress while a file goes up,
 * validation while the server reads it, and the outcome. It stays on every screen, so leaving
 * the New e-file page loses nothing.
 */
export function UploadsTray() {
  const tasks = useSyncExternalStore(uploadManager.subscribe, uploadManager.getSnapshot, uploadManager.getSnapshot);
  const [open, setOpen] = useState(false);
  useOutcomeToasts(tasks);
  if (tasks.length === 0) return null;

  const active = tasks.filter(isActive);
  const attention = tasks.filter((task) => task.phase === 'interrupted' || task.phase === 'blocked' || task.phase === 'failed');
  const uploading = tasks.find((task) => task.phase === 'uploading');
  const checking = active.some((task) => task.phase === 'uploaded' || task.phase === 'processing');
  const ringPercent = uploading ? uploading.percent : active.length > 0 ? 100 : 0;

  return (
    <div className="relative">
      <Tooltip
        content={
          uploading
            ? `Uploading ${uploading.fileName}`
            : checking
              ? 'Checking the file. You will be told when it is done.'
              : active.length > 0
                ? `${active.length} upload${active.length === 1 ? '' : 's'} in progress`
                : 'Uploads'
        }
      >
        <button
          type="button"
          aria-label="Uploads"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className={cn(
            'relative inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-2',
            'transition-colors duration-150 ease-out hover:bg-[color:var(--ts-row-hover)] hover:text-primary',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--ts-focus-ring)]',
          )}
        >
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full"
            style={{
              background: `conic-gradient(var(--ts-primary) ${ringPercent}%, transparent 0)`,
              mask: 'radial-gradient(circle, transparent 60%, black 62%)',
              WebkitMask: 'radial-gradient(circle, transparent 60%, black 62%)',
              opacity: active.length > 0 ? 1 : 0.25,
              animation: checking && !uploading ? 'spin 1.6s linear infinite' : undefined,
            }}
          />
          <CloudUpload className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden="true" />
          {attention.length > 0 ? (
            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-[color:var(--ts-pill-danger-text)]" />
          ) : null}
        </button>
      </Tooltip>

      {open ? (
        <div
          role="dialog"
          aria-label="Uploads"
          className="absolute right-0 z-50 mt-2 w-[380px] rounded-lg border border-[color:var(--ts-border-soft)] bg-surface p-3 shadow-elevated"
        >
          <div className="mb-2 flex items-center justify-between">
            <p className="text-base font-medium text-gray-3">Uploads</p>
            <button type="button" aria-label="Close" onClick={() => setOpen(false)} className="rounded-md p-1 text-gray-1 hover:text-gray-3">
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
          <ul className="flex max-h-[60vh] flex-col gap-2 overflow-y-auto">
            {tasks.map((task) => (
              <TaskRow key={task.id} task={task} onNavigate={() => setOpen(false)} />
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function TaskRow({ task, onNavigate }: { task: UploadTask; onNavigate: () => void }) {
  const toast = useToast();
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const line =
    task.phase === 'uploading'
      ? `Uploading ${formatBytes(task.loaded)} of ${formatBytes(task.size)}`
      : task.phase === 'uploaded'
        ? 'Uploaded. Handing the file to Tab Service.'
        : task.phase === 'processing'
          ? 'Reading every record and applying the IRS rules.'
          : task.phase === 'checked'
            ? (task.note ?? 'Ready to submit')
            : task.phase === 'blocked'
              ? `Cannot be submitted as it is${task.note ? `: ${task.note}` : ''}`
              : task.phase === 'failed'
                ? `Could not be read${task.note ? `: ${task.note}` : ''}`
                : (task.error ?? 'Interrupted. Choose the same file again to continue.');

  return (
    <li className="rounded-md border border-[color:var(--ts-border-soft)] px-3 py-2">
      <div className="flex items-start gap-2">
        {task.phase === 'checked' ? (
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--ts-pill-success-text)]" aria-hidden="true" />
        ) : task.phase === 'blocked' || task.phase === 'failed' || task.phase === 'interrupted' ? (
          <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--ts-pill-danger-text)]" aria-hidden="true" />
        ) : (
          <CloudUpload className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-base text-gray-3" title={task.fileName}>
            {task.fileName}
          </p>
          <p className="text-sm text-muted-foreground">{line}</p>
          {task.phase === 'uploading' ? (
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={task.percent} aria-valuemin={0} aria-valuemax={100}>
              <div className="h-full rounded-full bg-primary transition-[width] duration-150 ease-out" style={{ width: `${task.percent}%` }} />
            </div>
          ) : null}
        </div>
        <button type="button" aria-label={`Dismiss ${task.fileName}`} onClick={() => uploadManager.dismiss(task.id)} className="rounded-md p-1 text-gray-1 hover:text-gray-3">
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {task.phase === 'checked' || task.phase === 'processing' || task.phase === 'uploaded' || task.phase === 'blocked' ? (
          <Button asChild variant="outline" size="sm">
            <Link to="/e-files/new" search={{ uploadId: task.id }} onClick={onNavigate}>
              Open
            </Link>
          </Button>
        ) : null}
        {task.phase === 'interrupted' ? (
          <>
            <input
              ref={fileInput}
              type="file"
              className="hidden"
              onChange={async (event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (!file) return;
                setBusy(true);
                try {
                  await uploadManager.resume(task.id, file);
                } catch (error) {
                  toast.error('Could not continue the upload', errorMessage(error));
                } finally {
                  setBusy(false);
                }
              }}
            />
            <Button
              variant="primary"
              size="sm"
              disabled={busy}
              onClick={() => {
                if (uploadManager.fileFor(task.id)) {
                  setBusy(true);
                  void uploadManager.retry(task.id).catch((error) => toast.error('Could not continue the upload', errorMessage(error))).finally(() => setBusy(false));
                } else {
                  fileInput.current?.click();
                }
              }}
            >
              {uploadManager.fileFor(task.id) ? 'Retry' : 'Choose the file to continue'}
            </Button>
          </>
        ) : null}
      </div>
    </li>
  );
}

/**
 * A file that finished checking while the person was elsewhere is announced once, with a
 * way back to the step it is waiting at. Nothing is announced on the New e-file page for
 * the upload it is showing, where the step already says so.
 */
function useOutcomeToasts(tasks: UploadTask[]): void {
  const toast = useToast();
  const seen = useRef<Map<string, UploadTask['phase']>>(new Map());
  useEffect(() => {
    const onWizard = window.location.pathname.startsWith('/e-files/new');
    for (const task of tasks) {
      const before = seen.current.get(task.id);
      seen.current.set(task.id, task.phase);
      if (before === undefined || before === task.phase) continue;
      if (onWizard && window.location.search.includes(task.id)) continue;
      const open = (
        <Button asChild variant="outline" size="sm">
          <Link to="/e-files/new" search={{ uploadId: task.id }}>
            Open
          </Link>
        </Button>
      );
      if (task.phase === 'checked') {
        toast.show({ title: 'File checked', description: `${task.fileName}: ${task.note ?? 'ready to submit'}.`, tone: 'success', action: open, duration: 12_000 });
      } else if (task.phase === 'blocked') {
        toast.show({ title: 'File needs attention', description: `${task.fileName}${task.note ? `: ${task.note}` : ''}`, tone: 'warning', action: open, duration: 15_000 });
      } else if (task.phase === 'failed') {
        toast.show({ title: 'File could not be read', description: `${task.fileName}${task.note ? `: ${task.note}` : ''}`, tone: 'danger', action: open, duration: 15_000 });
      }
    }
  }, [tasks, toast]);
}
